import { useEffect, useRef, useState } from 'react';
import './AdminTextos.css';

const NIVEIS     = ['beginner', 'intermediate', 'advanced'];
const CATEGORIAS = ['General', 'Story', 'Culture', 'Health', 'Society', 'Conversation', 'Personal Growth', 'Nature', 'Lifestyle'];

const EMPTY_FORM = { titulo: '', conteudo: '', nivel: 'intermediate', categoria: 'General', idioma: 'en', ativo: true };

function formatDate(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

async function apiFetch(path, opts = {}) {
  const res = await fetch(path, { credentials: 'include', ...opts });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || `HTTP ${res.status}`);
  }
  return res.json();
}

export default function AdminTextos() {
  const [textos,      setTextos]      = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [formOpen,    setFormOpen]    = useState(false);
  const [editingId,   setEditingId]   = useState(null);
  const [form,        setForm]        = useState(EMPTY_FORM);
  const [saving,      setSaving]      = useState(false);
  const [confirmId,   setConfirmId]   = useState(null);
  const [toast,       setToast]       = useState(null);
  const [search,      setSearch]      = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const toastTimer = useRef(null);

  function showToast(msg, type = 'success') {
    setToast({ msg, type });
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }

  async function loadTextos() {
    setLoading(true);
    try {
      const data = await apiFetch('/api/textos/all');
      setTextos(data);
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadTextos(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  function openCreate() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormOpen(true);
  }

  async function openEdit(id) {
    try {
      // Busca o texto completo (incluindo conteudo)
      const res = await fetch(`/api/textos/${id}?admin=1`, { credentials: 'include' });
      // Fallback: buscar da lista
      const t = textos.find(x => x.id === id);
      const full = res.ok ? await res.json() : null;

      setForm({
        titulo:    full?.titulo    || t?.titulo    || '',
        conteudo:  full?.conteudo  || '',
        nivel:     full?.nivel     || t?.nivel     || 'intermediate',
        categoria: full?.categoria || t?.categoria || 'General',
        idioma:    full?.idioma    || t?.idioma    || 'en',
        ativo:     full !== null ? (full.ativo === 1 || full.ativo === true) : (t?.ativo === 1),
      });
      setEditingId(id);
      setFormOpen(true);
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  function closeForm() {
    setFormOpen(false);
    setEditingId(null);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.titulo.trim() || !form.conteudo.trim()) {
      showToast('Título e conteúdo são obrigatórios.', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await apiFetch(`/api/textos/${editingId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        showToast('Texto atualizado!');
      } else {
        await apiFetch('/api/textos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        showToast('Texto criado!');
      }
      closeForm();
      loadTextos();
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    try {
      await apiFetch(`/api/textos/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      showToast('Texto excluído.');
      loadTextos();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  async function toggleAtivo(t) {
    try {
      // Busca conteudo para não enviar vazio
      const res = await fetch(`/api/textos/${t.id}?admin=1`, { credentials: 'include' });
      const full = res.ok ? await res.json() : null;
      await apiFetch(`/api/textos/${t.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          titulo:    t.titulo,
          conteudo:  full?.conteudo || '.',
          nivel:     t.nivel,
          categoria: t.categoria,
          idioma:    t.idioma,
          ativo:     !t.ativo,
        }),
      });
      showToast(t.ativo ? 'Texto desativado.' : 'Texto ativado!');
      loadTextos();
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  // Filtros
  const filtered = textos.filter(t => {
    const matchSearch = !search || t.titulo.toLowerCase().includes(search.toLowerCase()) || (t.categoria || '').toLowerCase().includes(search.toLowerCase());
    const matchNivel  = !filterNivel || t.nivel === filterNivel;
    return matchSearch && matchNivel;
  });

  const stats = {
    total:   textos.length,
    ativos:  textos.filter(t => t.ativo).length,
    beg:     textos.filter(t => t.nivel === 'beginner').length,
    int:     textos.filter(t => t.nivel === 'intermediate').length,
    adv:     textos.filter(t => t.nivel === 'advanced').length,
  };

  // ── Render ────────────────────────────────────────────────
  return (
    <div className="at-page">

      {/* Header */}
      <div className="at-header">
        <h1><i className="fas fa-book-reader" /> Gerenciar Textos de Prática</h1>
        <button className="at-btn at-btn-primary" onClick={openCreate}>
          <i className="fas fa-plus" /> Novo Texto
        </button>
      </div>

      {/* Stats */}
      <div className="at-stats">
        <span className="at-stat"><i className="fas fa-list" /> {stats.total} textos</span>
        <span className="at-stat"><i className="fas fa-check-circle" /> {stats.ativos} ativos</span>
        <span className="at-stat"><i className="fas fa-seedling" /> {stats.beg} Beginner</span>
        <span className="at-stat"><i className="fas fa-chart-line" /> {stats.int} Intermediate</span>
        <span className="at-stat"><i className="fas fa-fire" /> {stats.adv} Advanced</span>
      </div>

      {/* Form panel */}
      {formOpen && (
        <div className="at-form-panel">
          <div className="at-form-title">
            <i className={editingId ? 'fas fa-edit' : 'fas fa-plus-circle'} />
            {editingId ? 'Editar Texto' : 'Novo Texto'}
          </div>

          <form onSubmit={handleSubmit}>
            {/* Título */}
            <div className="at-field" style={{ marginBottom: 14 }}>
              <label>Título <span style={{ color: '#ef4444' }}>*</span></label>
              <input
                type="text"
                placeholder="Ex: Sam and Leo — The Treasure"
                value={form.titulo}
                onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))}
                required
              />
            </div>

            <div className="at-grid2">
              {/* Nível */}
              <div className="at-field">
                <label>Nível</label>
                <select value={form.nivel} onChange={e => setForm(f => ({ ...f, nivel: e.target.value }))}>
                  {NIVEIS.map(n => <option key={n} value={n}>{n.charAt(0).toUpperCase() + n.slice(1)}</option>)}
                </select>
              </div>

              {/* Categoria */}
              <div className="at-field">
                <label>Categoria</label>
                <select value={form.categoria} onChange={e => setForm(f => ({ ...f, categoria: e.target.value }))}>
                  {CATEGORIAS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {/* Idioma */}
              <div className="at-field">
                <label>Idioma</label>
                <select value={form.idioma} onChange={e => setForm(f => ({ ...f, idioma: e.target.value }))}>
                  <option value="en">Inglês (en)</option>
                  <option value="pt">Português (pt)</option>
                  <option value="es">Espanhol (es)</option>
                </select>
              </div>

              {/* Ativo */}
              <div className="at-field" style={{ justifyContent: 'flex-end' }}>
                <label>Status</label>
                <label className="at-toggle">
                  <input
                    type="checkbox"
                    checked={form.ativo}
                    onChange={e => setForm(f => ({ ...f, ativo: e.target.checked }))}
                  />
                  <div className="at-toggle-track">
                    <div className="at-toggle-thumb" />
                  </div>
                  {form.ativo ? 'Ativo' : 'Inativo'}
                </label>
              </div>
            </div>

            {/* Conteúdo */}
            <div className="at-field at-field-full" style={{ marginBottom: 4 }}>
              <label>Conteúdo em Inglês <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea
                placeholder="Cole ou escreva o texto completo em inglês aqui..."
                value={form.conteudo}
                onChange={e => setForm(f => ({ ...f, conteudo: e.target.value }))}
                required
              />
            </div>
            <div className="at-char-count">{form.conteudo.length} caracteres · {form.conteudo.split(/\s+/).filter(Boolean).length} palavras</div>

            <div className="at-form-actions">
              <button type="button" className="at-btn at-btn-secondary" onClick={closeForm}>
                Cancelar
              </button>
              <button type="submit" className="at-btn at-btn-primary" disabled={saving}>
                <i className={`fas fa-${saving ? 'spinner fa-spin' : 'check'}`} />
                {saving ? 'Salvando...' : 'Salvar Texto'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter bar */}
      <div className="at-filter">
        <input
          placeholder="Buscar por título ou categoria..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select value={filterNivel} onChange={e => setFilterNivel(e.target.value)}>
          <option value="">Todos os níveis</option>
          {NIVEIS.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span style={{ fontSize: '0.82rem', color: '#94a3b8', marginLeft: 4 }}>
          {filtered.length}/{textos.length}
        </span>
      </div>

      {/* List */}
      {loading ? (
        <div className="at-empty"><i className="fas fa-spinner fa-spin" /><p>Carregando textos...</p></div>
      ) : filtered.length === 0 ? (
        <div className="at-empty"><i className="fas fa-inbox" /><p>Nenhum texto encontrado.</p></div>
      ) : (
        filtered.map(t => (
          <div key={t.id} className={`at-card${!t.ativo ? ' inactive' : ''}`}>
            <div className="at-card-info">
              <div className="at-card-title">{t.titulo}</div>
              <div className="at-card-meta">
                <span className={`at-level ${t.nivel}`}>{t.nivel}</span>
                {t.categoria && <span style={{ color: '#64748b' }}>{t.categoria}</span>}
                <span><i className="fas fa-calendar" style={{ marginRight: 3 }} />{formatDate(t.created_at)}</span>
                {!t.ativo && <span style={{ color: '#ef4444', fontWeight: 700 }}><i className="fas fa-eye-slash" /> Inativo</span>}
              </div>
            </div>
            <div className="at-card-actions">
              <button
                className={`at-btn ${t.ativo ? 'at-btn-secondary' : 'at-btn-success'}`}
                onClick={() => toggleAtivo(t)}
                title={t.ativo ? 'Desativar' : 'Ativar'}
                style={{ padding: '8px 10px' }}
              >
                <i className={`fas fa-${t.ativo ? 'eye-slash' : 'eye'}`} />
              </button>
              <button className="at-btn at-btn-warning" onClick={() => openEdit(t.id)}>
                <i className="fas fa-edit" /> Editar
              </button>
              <button className="at-btn at-btn-danger" onClick={() => setConfirmId(t.id)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        ))
      )}

      {/* Confirm modal */}
      {confirmId && (
        <div className="at-modal-backdrop">
          <div className="at-modal">
            <h3>Excluir texto?</h3>
            <p>Esta ação não pode ser desfeita.</p>
            <div className="at-modal-actions">
              <button className="at-btn at-btn-secondary" onClick={() => setConfirmId(null)}>Cancelar</button>
              <button className="at-btn at-btn-danger" onClick={() => handleDelete(confirmId)}>
                <i className="fas fa-trash" /> Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <div className={`at-toast ${toast.type}`}>{toast.msg}</div>}
    </div>
  );
}
